import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Typography, List, ListItem, ListItemText, Box, Snackbar,
  IconButton, Divider, Container, Grid, Button, Alert
} from '@mui/material';
import { CopyToClipboard } from 'react-copy-to-clipboard';

// Ícones existentes e novos para os parâmetros adicionados
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import InfoIcon from '@mui/icons-material/Info';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SecurityIcon from '@mui/icons-material/Security';
import GppGoodIcon from '@mui/icons-material/GppGood';
import LinkIcon from '@mui/icons-material/Link';
import ExploreIcon from '@mui/icons-material/Explore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoDevIcon from '@mui/icons-material/LogoDev';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';

import GoTopo from './GoTopo';
import { camara, gurjao } from './adm/admin/firebaseConfig';

function Footer({ config }) {
  const [open, setOpen] = useState(false);
  const [copiedText, setCopiedText] = useState('');

  const handleCopy = (text) => {
    setCopiedText(text);
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  // Estilo base para links com efeito hover premium de transição lateral suave
  const linkHoverStyle = {
    textDecoration: 'none',
    color: '#FFFFFF',
    transition: 'all 0.2s ease-in-out',
    display: 'inline-block',
    '&:hover': {
      color: config.colorSecondary || '#0F71FD',
      transform: 'translateX(4px)'
    }
  };

  // Define dinamicamente o tamanho das colunas baseado na visibilidade dos blocos (Prefeitura vs Câmara)
  const columnSize = camara ? { xs: 12, sm: 6, md: 4 } : { xs: 12, sm: 6, md: 3 };

  return (
    <>
      <GoTopo config={config} />
      <Box
        component="footer"
        sx={{
          backgroundColor: config.color || '#121212',
          padding: '48px 0 24px 0',
          color: '#FFFFFF',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>

            {/* 1. SEÇÃO SOBRE (Controlado por config.design.sobre) */}
            {config?.design?.sobre !== false && (
              <Grid item xs={12} md={columnSize.md}>
                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Sobre <InfoIcon fontSize="small" />
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'justify', opacity: 0.85, lineHeight: 1.6 }}>
                  {config.resumoHistorico || "Histórico institucional do município."}
                </Typography>
                <Box display="flex" justifyContent="flex-start" mt={2.5}>
                  <Button
                    variant="contained"
                    startIcon={<ExploreIcon />}
                    component={Link}
                    to="/historia"
                    sx={{
                      color: config.color,
                      backgroundColor: '#FFFFFF',
                      fontWeight: 'bold',
                      borderRadius: '8px',
                      textTransform: 'none',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '&:hover': {
                        color: '#FFFFFF',
                        backgroundColor: config.colorSecondary || '#0F71FD'
                      }
                    }}
                  >
                    Conhecer História
                  </Button>
                </Box>
              </Grid>
            )}

            {/* 2. SEÇÃO DOCUMENTOS (Exclusivo Prefeitura - Controlado por config.design.documentos) */}
            {!camara && config?.design?.documentos !== false && (
              <Grid item {...columnSize}>
                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Documentos <ArticleIcon fontSize="small" />
                </Typography>
                <List dense sx={{ p: 0 }}>
                  {[
                    { path: "/arquivos/contratos", title: "Contratos" },
                    { path: "/legislacao/decretos", title: "Decretos" },
                    { path: "/arquivos/editais", title: "Editais" },
                    { path: "/arquivos/farmaciabasica", title: "Estoque Farmácia Básica" },
                    { path: "/arquivos/frota", title: "Frota Municipal" },
                    { path: "/legislacao/portarias", title: "Portarias" },
                    { path: "/arquivos/quadro", title: "Quadro Funcional" },
                    { path: "/processo_seletivo", title: "Processo Seletivo" }
                  ].map((item) => (
                    <ListItem key={item.title} disablePadding sx={{ mb: 1 }}>
                      <ListItemText
                        primary={
                          <Box component={Link} to={item.path} sx={linkHoverStyle}>
                            {item.title}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}

            {/* 3. SEÇÃO LINKS ÚTEIS E NOVOS SERVIÇOS EXTERNOS */}
            {config?.design?.documentos !== false && (
              <Grid item {...columnSize}>
                <Typography variant="h6" fontWeight="700" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Links Úteis <LinkIcon fontSize="small" />
                </Typography>

                <List dense sx={{ p: 0 }}>
                  {/* Links Estruturais Internos (Mantendo a separação Câmara/Prefeitura) */}
                  {camara ? (
                    <>
                      {[
                        { path: "/legislacao/requerimentos", title: "Requerimentos" },
                        { path: "/legislacao/leismunicipais", title: "Leis Municipais" },
                        { path: "/transparencia", title: "Transparência Fiscal" },
                        { path: "/legislacao/projetodelei", title: "Projetos de Lei" }
                      ].map((item) => (
                        <ListItem key={item.title} disablePadding sx={{ mb: 1 }}>
                          <ListItemText primary={<Box component={Link} to={item.path} sx={linkHoverStyle}>{item.title}</Box>} />
                        </ListItem>
                      ))}
                    </>
                  ) : (
                    <>
                      {[
                        { path: "/arquivos/contratos", title: "Contratos" },
                        { path: "/arquivos/frota", title: "Frota Municipal" },
                        { path: "/transparencia", title: "Transparência Fiscal" },
                        { path: "/legislacao/codigotributario", title: "Código Tributário" }
                      ].map((item) => (
                        <ListItem key={item.title} disablePadding sx={{ mb: 1 }}>
                          <ListItemText primary={<Box component={Link} to={item.path} sx={linkHoverStyle}>{item.title}</Box>} />
                        </ListItem>
                      ))}

                      {/* Contracheque Interno ou Externo com base nas novas configurações */}
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemText
                          primary={
                            config?.links?.cc ? (
                              <Box component="a" href={config.links.cc} target="_blank" rel="noopener noreferrer" sx={linkHoverStyle}>Contra-cheque (Portal)</Box>
                            ) : (
                              <Box component={Link} to="/rh/cc" sx={linkHoverStyle}>Contra-cheque</Box>
                            )
                          }
                        />
                      </ListItem>
                    </>
                  )}

                  {/* NOVOS: Links Externos Cadastrados via Parâmetros (Apenas se existirem) */}
                  {!camara && config?.links?.iptu && (
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemText primary={<Box component="a" href={config.links.iptu} target="_blank" rel="noopener noreferrer" sx={linkHoverStyle}>Portal IPTU</Box>} />
                    </ListItem>
                  )}
                  {!camara && config?.links?.notafiscal && (
                    <ListItem disablePadding sx={{ mb: 1 }}>
                      <ListItemText primary={<Box component="a" href={config.links.notafiscal} target="_blank" rel="noopener noreferrer" sx={linkHoverStyle}>Nota Fiscal Eletrônica</Box>} />
                    </ListItem>
                  )}
                </List>

                {gurjao && (
                  <>
                    <Typography variant="subtitle1" fontWeight="700" sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      Transparência <AssessmentIcon fontSize="small" />
                    </Typography>
                    <List dense sx={{ p: 0, mt: 0.5 }}>
                      <ListItem disablePadding>
                        <ListItemText primary={<Box component={Link} to="/transparencia" sx={linkHoverStyle}>Nota máxima no TRE-PB - Turmalina</Box>} />
                      </ListItem>
                    </List>
                  </>
                )}
              </Grid>
            )}

            {/* 4. SEÇÃO CONTATOS, ENDEREÇO E HORÁRIO (Controlado por config.design.contatos) */}
            {config?.design?.contatos !== false && (
              <Grid item xs={12} md={columnSize.md}>
                <Typography variant="h6" fontWeight="700" gutterBottom>
                  Contato e Atendimento
                </Typography>

                {/* E-mail com clique para cópia */}
                {config?.email && (
                  <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CopyToClipboard text={config.email} onCopy={() => handleCopy('E-mail')}>
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { color: config.colorSecondary || '#0F71FD' } }}>
                        <EmailIcon fontSize="small" sx={{ color: config.colorSecondary || '#0F71FD' }} /> {config.email}
                      </Box>
                    </CopyToClipboard>
                  </Typography>
                )}

                {/* Telefone com clique para cópia */}
                {config?.telefone && (
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CopyToClipboard text={config.telefone} onCopy={() => handleCopy('Telefone')}>
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { color: config.colorSecondary || '#0F71FD' } }}>
                        <PhoneIcon fontSize="small" sx={{ color: config.colorSecondary || '#0F71FD' }} /> {config.telefone}
                      </Box>
                    </CopyToClipboard>
                  </Typography>
                )}

                {/* NOVO: Exibição do Endereço Completo */}
                {config?.endereco && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, opacity: 0.85 }}>
                    <LocationOnIcon fontSize="small" sx={{ color: config.colorSecondary || '#0F71FD', mt: 0.3, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{config.endereco}</Typography>
                  </Box>
                )}

                {/* NOVO: Exibição do Horário de Atendimento */}
                {config?.horarioAtendimento && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, opacity: 0.85 }}>
                    <AccessTimeIcon fontSize="small" sx={{ color: config.colorSecondary || '#0F71FD', mt: 0.3, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                      <strong>Atendimento:</strong><br />{config.horarioAtendimento}
                    </Typography>
                  </Box>
                )}

                {/* Selo LGPD e Segurança */}
                {!camara && (
                  <>
                    <Divider sx={{ marginY: '15px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', opacity: 0.8 }}>
                      <SecurityIcon fontSize="small" sx={{ color: '#4CAF50', mt: 0.2 }} />
                      <Box>
                        <Link to="/lgpd" style={{ textDecoration: 'none', color: '#FFFFFF' }}>
                          <Typography variant="caption" sx={{ '&:hover': { underline: 'always' }, display: 'block', lineHeight: 1.3 }}>
                            Este site cumpre as diretrizes da LGPD (Lei Geral de Proteção de Dados).
                          </Typography>
                        </Link>
                      </Box>
                    </Box>
                  </>
                )}

                {gurjao && (
                  <>
                    <Divider sx={{ marginY: '15px', backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <Box component="a" href="/certificado_ssl/gurjao.pb.gov.br.crt" download style={{ textDecoration: 'none', color: '#FFFFFF' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8, '&:hover': { opacity: 1 } }}>
                        <GppGoodIcon fontSize="small" sx={{ color: '#00E676' }} />
                        <Typography variant="caption" fontWeight="600">Certificado Digital de Segurança</Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {camara && (<>
                  <a
                    href="https://radardatransparencia.atricon.org.br/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {/* IMAGEM ADICIONADA ABAIXO DOS ITENS DE ATENDIMENTO */}
                    <Box
                      component="img"
                      src="/imagens/radar-transparencia.png"
                      // src={config?.urlImagemContato || "https://via.placeholder.com/100"}
                      alt="Identificação Atendimento"
                      sx={{
                        width: '250px',
                        height: 'auto',
                        mt: 2.5,
                        display: 'block',
                        opacity: 0.9,
                        transition: 'opacity 0.2s',
                        '&:hover': { opacity: 1 }
                      }}
                    />
                  </a></>)}

              </Grid>
            )}
          </Grid>

          <Divider sx={{ marginY: '32px', backgroundColor: 'rgba(255,255,255,0.1)' }} />

          {/* BARRA INFERIOR: COPYRIGHT, CNPJ E REDES SOCIAIS */}
          <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12} sm={8}>
              <Typography variant="body2" sx={{ opacity: 0.75, textAlign: { xs: 'center', sm: 'left' } }}>
                {config.footer ? (
                  config.footer
                ) : (
                  `© ${new Date().getFullYear()} - ${config.orgaoNome || 'Município'}`
                )}
                {config.cnpj && ` | CNPJ: ${config.cnpj}`}
              </Typography>
              {config.dev && (
                <Typography
                  variant="caption"
                  sx={{
                    opacity: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    mt: 0.5
                  }}
                >
                  <LogoDevIcon fontSize="inherit" />
                  Desenvolvimento:{' '}
                  <a
                    href="https://www.bigstarsoftwares.com.br"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none' }}
                  >
                    {config.dev}
                  </a>
                </Typography>
              )}
            </Grid>

            {config?.design?.socialMedia !== false && config?.links && (
              <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                <Box display="flex" gap={1}>
                  {[
                    { icon: <FacebookIcon />, link: config.links.facebook },
                    { icon: <InstagramIcon />, link: config.links.instagram },
                    { icon: <YouTubeIcon />, link: config.links.youtube },
                    { icon: <WhatsAppIcon />, link: config.links.whatsapp }
                  ].map((social, index) => {

                    if (!social.link) return null;
                    return (
                      <IconButton
                        key={index}
                        color="inherit"
                        component="a"
                        href={social.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.05)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: config.colorSecondary || '#0F71FD',
                            transform: 'translateY(-3px)'
                          }
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    );
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleClose} severity="success" variant="filled" sx={{ borderRadius: '8px', fontWeight: 'bold' }}>
          {copiedText} copiado para a área de transferência!
        </Alert>
      </Snackbar>
    </>
  );
}

export default Footer;